import { useEffect, useState } from "react";
import { appMeta } from "../api/client";
import { IS_NATIVE } from "../lib/native";

/** True when `current` is older than `min` (numeric dotted compare). */
function isOlder(current: string, min: string): boolean {
  const a = current.split(".").map((n) => parseInt(n, 10) || 0);
  const b = min.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d < 0;
  }
  return false;
}

/**
 * Startup update gate. Asks the server for the minimum allowed bundle
 * version; when this build is older, the app is blocked behind a full-screen
 * "Update erforderlich" panel. Web clients get one silent hard reload first
 * (usually enough — the server already serves the new bundle); native (APK)
 * clients get the download link.
 *
 * Fails open: if the endpoint is unreachable, the app just starts.
 */
export default function UpdateGate() {
  const [updateUrl, setUpdateUrl] = useState<string | null>(null);

  useEffect(() => {
    appMeta
      .version()
      .then(({ min_version, update_url }) => {
        if (!min_version || !isOlder(__APP_VERSION__, min_version)) return;
        if (!IS_NATIVE) {
          // The web server already has the new bundle — a single hard reload
          // per min_version bump gets it. Guard via sessionStorage so a
          // still-stale cache can't reload-loop.
          const KEY = "logbuch-update-reloaded-for";
          if (window.sessionStorage.getItem(KEY) !== min_version) {
            window.sessionStorage.setItem(KEY, min_version);
            window.location.reload();
            return;
          }
        }
        setUpdateUrl(update_url);
      })
      .catch(() => {
        /* offline / endpoint missing → start normally */
      });
  }, []);

  if (!updateUrl) return null;
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-ink-950 px-6">
      <div className="card max-w-sm w-full p-6 space-y-4 text-center">
        <div className="text-4xl">⬆️</div>
        <h1 className="text-lg font-semibold text-ink-50">
          Update erforderlich
        </h1>
        <p className="text-sm text-ink-300">
          Deine App-Version ist veraltet.
          {IS_NATIVE
            ? " Lade die neue Version herunter und installiere sie — deine Daten bleiben erhalten."
            : " Lade die Seite neu, um die aktuelle Version zu bekommen."}
        </p>
        {IS_NATIVE ? (
          <a
            href={updateUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-primary inline-block"
          >
            Update herunterladen
          </a>
        ) : (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Neu laden
          </button>
        )}
      </div>
    </div>
  );
}
